import type { IPointerController } from '../PointerController';
import type { ICameraModifier, IPointerAction } from '../PointerHandlerAdjustCamera';
import type { PointerSyntheticEvent } from '../PointerSyntheticEvent';
import type * as THREE from 'three';
import type { IRaycaster } from '@/engine/app/interfaces';

enum _Mode {
  NONE = 0,
  ROTATE = 1,
  ROTATE_IN_PLANE = 2,
  ZOOM = 3,
  PAN = 4
}

enum _Cursor {
  NONE = 'default',
  ROTATE = 'grabbing',
  //ROTATE_IN_PLANE = 'grabbing',
  //ZOOM = 'default',
  PAN = 'all-scroll'
}

export class MousePointerAction implements IPointerAction {
  private readonly _rayCaster: IRaycaster;

  private readonly _cameraModifier: ICameraModifier;

  private readonly _pointerController: IPointerController;

  private readonly _noRotate: boolean;

  private _activeMode = _Mode.NONE;

  private _cursorIcon = _Cursor.NONE;

  private _startCursor2d?: THREE.Vector2;

  private _rayCastedPoint3d?: THREE.Vector3;

  private _prevCursor2d?: THREE.Vector2;

  private readonly _rotateInPlanePercentage = 0.05;

  private readonly _rotateAroundCenterZoom;

  public constructor(
    rayCaster: IRaycaster,
    cameraModifier: ICameraModifier,
    pointerController: IPointerController,
    noRotate: boolean,
    rotateAroundCenterZoom: number
  ) {
    this._rayCaster = rayCaster;
    this._pointerController = pointerController;
    this._cameraModifier = cameraModifier;
    this._noRotate = noRotate;
    this._rotateAroundCenterZoom = rotateAroundCenterZoom;
  }

  public addPointer(event: PointerSyntheticEvent): void {
    this._startCursor2d = this._cameraModifier.getPointerEventCoordinates(event.originalEvent);

    this._prevCursor2d = this._startCursor2d.clone();
    if (this._cameraModifier.getCameraZoom() > this._rotateAroundCenterZoom) {
      this._rayCastedPoint3d = this._rayCaster.rayCast(this._startCursor2d)?.point;
    } else {
      this._rayCastedPoint3d = undefined;
    }
    switch (event.originalEvent.button) {
      case 0:
        if (!this._noRotate) {
          const dim = this._cameraModifier.getFrameDimensions();
          if (
            this._startCursor2d.x < dim.x * this._rotateInPlanePercentage ||
            this._startCursor2d.x > dim.x * (1 - this._rotateInPlanePercentage) ||
            this._startCursor2d.y < dim.y * this._rotateInPlanePercentage ||
            this._startCursor2d.y > dim.y * (1 - this._rotateInPlanePercentage)
          ) {
            this._activeMode = _Mode.ROTATE_IN_PLANE;
          } else {
            this._activeMode = _Mode.ROTATE;
          }
          this._cursorIcon = _Cursor.ROTATE;
        }
        break;
      case 1:
        this._activeMode = _Mode.ZOOM;
        this._cursorIcon = _Cursor.NONE;
        break;
      case 2:
        this._activeMode = _Mode.PAN;
        this._cursorIcon = _Cursor.PAN;
        break;
    }
    this._pointerController.setCursor(this._cursorIcon);
  }

  public removePointer(/*event: PointerSyntheticEvent*/): boolean {
    this._activeMode = _Mode.NONE;
    this._cursorIcon = _Cursor.NONE;
    this._startCursor2d = undefined;
    this._prevCursor2d = undefined;
    this._pointerController.setCursor(this._cursorIcon);
    return true;
  }

  public updatePointer(event: PointerSyntheticEvent): boolean {
    if (this._activeMode == _Mode.NONE || !this._startCursor2d || !this._prevCursor2d) {
      return false;
    }
    const cursor2d = this._cameraModifier.getPointerEventCoordinates(event.originalEvent);
    switch (this._activeMode) {
      case _Mode.ROTATE:
        if (event.originalEvent.altKey) {
          this._cameraModifier.rotateCameraInPlane(this._prevCursor2d, cursor2d);
        } else if (event.originalEvent.ctrlKey) {
          this._cameraModifier.panCamera(this._prevCursor2d, cursor2d);
        } else if (event.originalEvent.shiftKey) {
          this._cameraModifier.zoomCamera(this._startCursor2d, this._prevCursor2d, cursor2d);
        } else {
          this._cameraModifier.rotateCamera(
            this._rayCastedPoint3d ?? this._cameraModifier.getCenterPoint(),
            this._prevCursor2d,
            cursor2d
          );
        }
        this._cursorIcon = _Cursor.ROTATE;
        break;
      case _Mode.ROTATE_IN_PLANE:
        if (event.originalEvent.altKey) {
          this._cameraModifier.rotateCamera(
            this._rayCastedPoint3d ?? this._cameraModifier.getCenterPoint(),
            this._prevCursor2d,
            cursor2d
          );
        } else if (event.originalEvent.ctrlKey) {
          this._cameraModifier.panCamera(this._prevCursor2d, cursor2d);
        } else if (event.originalEvent.shiftKey) {
          this._cameraModifier.zoomCamera(this._startCursor2d, this._prevCursor2d, cursor2d);
        } else {
          this._cameraModifier.rotateCameraInPlane(this._prevCursor2d, cursor2d);
        }
        this._cursorIcon = _Cursor.ROTATE; //ROTATE_IN_PLANE;
        break;
      case _Mode.PAN:
        if (event.originalEvent.altKey) {
          if (!this._noRotate) {
            this._cameraModifier.rotateCameraInPlane(this._prevCursor2d, cursor2d);
          }
        } else if (event.originalEvent.ctrlKey) {
          if (!this._noRotate) {
            this._cameraModifier.rotateCamera(
              this._rayCastedPoint3d ?? this._cameraModifier.getCenterPoint(),
              this._prevCursor2d,
              cursor2d
            );
          }
        } else if (event.originalEvent.shiftKey) {
          this._cameraModifier.zoomCamera(this._startCursor2d, this._prevCursor2d, cursor2d);
        } else {
          this._cameraModifier.panCamera(this._prevCursor2d, cursor2d);
        }
        this._cursorIcon = _Cursor.PAN;
        break;
      case _Mode.ZOOM:
        this._cameraModifier.zoomCamera(this._startCursor2d, this._prevCursor2d, cursor2d);
        this._cursorIcon = _Cursor.NONE; //ZOOM;
        break;
    }
    this._pointerController.setCursor(this._cursorIcon);
    this._prevCursor2d = cursor2d;
    return true;
  }
}
